import {
  GoabFormItem,
  GoabInputNumber,
  GoabInputOnChangeDetail,
  GoabText,
  GoaInputOnBlurDetail,
} from '@abgov/angular-components';
import {
  AddClickDirective,
  ChangeDirectionType,
  CycleTabBoundaryDirective,
} from '@ala/common-ui';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  HostBinding,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CapacityReportStatus } from '@libs/services/graphql/generated/ala-backend.service';
import {
  BedCountChangeEventArgs,
  BedCountTypes,
} from '../../viewmodels/bed-count-change-event-args';
import { IDataEntryRow } from '../../viewmodels/data-entry-row.viewmodel';

@Component({
  selector: 'app-data-entry-row',
  imports: [
    CommonModule,
    GoabInputNumber,
    AddClickDirective,
    CycleTabBoundaryDirective,
    GoabText,
    GoabFormItem,
  ],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './data-entry-row.component.html',
  styleUrl: './data-entry-row.component.scss',
})
export class DataEntryRowComponent implements AfterViewInit {
  readonly doShadeRow = computed(() => this.index() % 2 === 0);

  @HostBinding('class.shaded-row')
  get isShaded() {
    return this.doShadeRow();
  }

  media: MediaQueryList = matchMedia('(max-width: 1024px)');

  dataEntryRow = input.required<IDataEntryRow>();
  index = input.required<number>();
  bedTypeDescription = input.required<string>();
  fieldToFocus = input<number | null>(null);
  status = input<CapacityReportStatus | undefined>(undefined);
  // Total number of rows (needed for wrap-around tabbing to identify last field). Optional with default 1 for isolated tests.
  totalRows = input<number>(1);

  dataEntryRowChange = output<BedCountChangeEventArgs>();
  fieldChange = output<BedCountChangeEventArgs>();
  fieldSelection = output<{ row: number; col: BedCountTypes }>();
  tabDirection = output<ChangeDirectionType>();
  changing = output<number | undefined>(); // tracks the number of characters in the currently-changing input field

  capacityReportStatus = CapacityReportStatus; // make the enum available to the template

  // Simplified state management with single source of truth
  private readonly bedValues = signal<{
    operational: { current?: number | null; previous?: number | null };
    occupied: { current?: number | null; previous?: number | null };
  }>({
    operational: { current: undefined, previous: undefined },
    occupied: { current: undefined, previous: undefined },
  });

  private readonly focusState = signal<{
    operational: boolean;
    occupied: boolean;
  }>({
    operational: false,
    occupied: false,
  });

  validationStatus = output<{ hasError: boolean }>();

  // Computed properties for current display values
  readonly currentOperationalValue = computed(() => {
    const row = this.dataEntryRow();
    const bedValues = this.bedValues();

    if (!row) return undefined;

    // Use current value if it exists, otherwise use row value
    return bedValues.operational.current !== undefined
      ? bedValues.operational.current
      : row.operationalBeds;
  });

  readonly currentOccupiedValue = computed(() => {
    const row = this.dataEntryRow();
    const bedValues = this.bedValues();

    if (!row) return undefined;

    // Use current value if it exists, otherwise use row value
    return bedValues.occupied.current !== undefined
      ? bedValues.occupied.current
      : row.occupiedBeds;
  });

  hasOccupiedFootnote(): boolean {
    const dataEntryRow = this.dataEntryRow();
    const currentValue = this.currentOccupiedValue();

    if (!dataEntryRow.previousOccupiedBeds || currentValue === undefined) {
      return false;
    }

    const status = this.status();
    return (
      (status === CapacityReportStatus.COMPLETED ||
        status === CapacityReportStatus.INPROGRESS) &&
      dataEntryRow.previousOccupiedBeds !== currentValue
    );
  }

  hasOperationalFootnote(): boolean {
    const dataEntryRow = this.dataEntryRow();
    const currentValue = this.currentOperationalValue();

    if (!dataEntryRow.previousOperationalBeds || currentValue === undefined) {
      return false;
    }

    const status = this.status();
    return (
      (status === CapacityReportStatus.COMPLETED ||
        status === CapacityReportStatus.INPROGRESS) &&
      dataEntryRow.previousOperationalBeds !== currentValue
    );
  }

  bedTypeTextHeadingRef = viewChild.required('bedTypeTextHeading', {
    read: ElementRef,
  });
  bedTypeSubTextHeadingRef = viewChild.required('bedTypesubTextHeading', {
    read: ElementRef,
  });
  bedTypeDescriptionHeadingRef = viewChild.required(
    'bedTypeDescriptionHeading',
    {
      read: ElementRef,
    }
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  el: any;

  private inputFocusEffect = effect(() => {
    const fieldToFocus = this.fieldToFocus();
    if (fieldToFocus === null) return;
    const countType = ['operational', 'occupied'][fieldToFocus];

    // Use microtask + animation frame for more deterministic timing than a fixed 100ms timeout.
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        const host = document.querySelector(
          `#${countType}-beds-input-${this.index()}`
        ) as HTMLElement | null;
        const inputElement =
          host?.children?.[0]?.shadowRoot?.querySelector('input');
        if (inputElement instanceof HTMLInputElement) {
          inputElement.focus({ preventScroll: true });
          inputElement.select();
        }
      });
    });
  });

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.el = this.bedTypeDescriptionHeadingRef()
        .nativeElement?.querySelector('goa-text')
        ?.shadowRoot?.querySelector('.body-xs');
      if (this.el) {
        this.el.style.display = 'block';
      }
      this.media = matchMedia('(max-width: 1024px)');
      this.media.addEventListener('change', this.adjustLineHeightCb);
    }, 100);
  }

  adjustLineHeightCb = () => {
    if (this.el && this.media) {
      if (this.media.matches) {
        this.el.style.lineHeight = '1.3';
      } else {
        this.el.style.lineHeight = '20px';
      }
    }
  };

  private getEventValue(
    value: string,
    currentValue: number | undefined
  ): number | null | undefined | boolean {
    const n = Number(value);
    if (isNaN(n)) {
      return false;
    }
    return value.trim() === ''
      ? currentValue === undefined
        ? undefined
        : null
      : n;
  }

  onOperationalBedsBlur($event: GoaInputOnBlurDetail) {
    this.updateFocusState('operational', false);
    this.changing.emit(undefined);

    const operationalBeds = this.dataEntryRow().operationalBeds;
    const newValue = this.getEventValue($event.value, operationalBeds);

    if (newValue === false) {
      return;
    }

    const numericValue = newValue as number | null;
    this.updateBedValue('operational', 'current', numericValue);

    this.onBlur(
      BedCountTypes.Operational,
      numericValue,
      numericValue !== operationalBeds
    );
  }

  onOccupiedBedsBlur($event: GoaInputOnBlurDetail) {
    this.updateFocusState('occupied', false);
    this.changing.emit(undefined);

    const occupiedBeds = this.dataEntryRow().occupiedBeds;
    const newValue = this.getEventValue($event.value, occupiedBeds);

    if (newValue === false) {
      return;
    }

    const numericValue = newValue as number | null;
    this.updateBedValue('occupied', 'current', numericValue);

    this.onBlur(
      BedCountTypes.Occupied,
      numericValue,
      numericValue !== occupiedBeds
    );
  }
  // this is called by the onBlur handlers,
  // either after tabbing or clicking
  private onBlur(
    countType: BedCountTypes,
    count: number | null,
    changed: boolean
  ) {
    const eventArgs: BedCountChangeEventArgs = {
      changedFieldValue: count,
      changedFieldCoords: { countType, bedType: this.dataEntryRow().bedType },
    };

    // Always emit validation status, even for invalid values
    this.validationStatus.emit({ hasError: this.validateEntryRow() });

    if (this.isNegativeOrFractional(count as number)) {
      return;
    }

    if (changed) {
      this.dataEntryRowChange.emit(eventArgs);
    } else {
      this.fieldChange.emit(eventArgs);
    }
  }

  onChanging($event: GoabInputOnChangeDetail) {
    this.changing.emit($event.value.trim().length);
  }

  onOperationalBedsChange($event: GoabInputOnChangeDetail) {
    if ($event.value !== this.currentOperationalValue()?.toString()) {
      this.changing.emit($event.value.trim().length);
      const newValue = this.getEventValue(
        $event.value,
        this.dataEntryRow().operationalBeds
      );
      this.updateBedValue('operational', 'current', newValue as number | null);
    }
  }

  onOccupiedBedsChange($event: GoabInputOnChangeDetail) {
    if ($event.value !== this.currentOccupiedValue()?.toString()) {
      this.changing.emit($event.value.trim().length);
      const newValue = this.getEventValue(
        $event.value,
        this.dataEntryRow().occupiedBeds
      );
      this.updateBedValue('occupied', 'current', newValue as number | null);
    }
  }

  // Helper methods for state management
  updateFocusState(type: 'operational' | 'occupied', focused: boolean) {
    this.focusState.update((state) => ({
      ...state,
      [type]: focused,
    }));
  }

  private updateBedValue(
    type: 'operational' | 'occupied',
    valueType: 'current' | 'previous',
    value: number | null | undefined
  ) {
    this.bedValues.update((state) => ({
      ...state,
      [type]: {
        ...state[type],
        [valueType]: value,
      },
    }));
  }

  // Validations
  private isNegativeOrFractional(value: number): boolean {
    return value < 0 || value % 1 !== 0;
  }

  private validateBedCount(
    currentValue: number | null | undefined,
    comparisonValue: number | undefined,
    label: string
  ) {
    if (currentValue === null || currentValue === undefined) {
      return { hasError: false, errorMessage: null };
    }
    const isGreaterThanComparison =
      currentValue !== undefined &&
      currentValue !== null &&
      comparisonValue !== undefined &&
      currentValue > comparisonValue;

    const isNegativeOrFractionalVal =
      currentValue !== undefined &&
      currentValue !== null &&
      this.isNegativeOrFractional(currentValue);

    const hasError = isGreaterThanComparison || isNegativeOrFractionalVal;

    let errorMessage: string | null = null;

    if (isGreaterThanComparison && isNegativeOrFractionalVal) {
      errorMessage = `Number cannot exceed ${label}. Number cannot be negative or fractional.`;
    } else if (isGreaterThanComparison) {
      errorMessage = `Number cannot exceed ${label}.`;
    } else if (isNegativeOrFractionalVal) {
      errorMessage = 'Number cannot be negative or fractional.';
    }

    return {
      hasError: hasError,
      errorMessage: errorMessage,
    };
  }

  // Updated validation computed properties
  readonly operationalBedsValidation = computed(() => {
    const operationalVal = this.currentOperationalValue();
    const fundedVal = this.dataEntryRow().fundedBeds;

    return this.validateBedCount(operationalVal, fundedVal, 'funded beds');
  });

  readonly occupiedBedsValidation = computed(() => {
    const occupiedVal = this.currentOccupiedValue();
    const operationalVal = this.currentOperationalValue();

    const maxValue = operationalVal ?? 0;

    return this.validateBedCount(occupiedVal, maxValue, 'operational beds');
  });

  validateEntryRow() {
    return (
      this.occupiedBedsValidation().hasError ||
      this.operationalBedsValidation().hasError
    );
  }

  readonly vacancy = computed(() => {
    const operational = this.currentOperationalValue() ?? 0;
    const occupied = this.currentOccupiedValue() ?? 0;
    return operational - occupied;
  });

  readonly showVacancy = computed(() => {
    const operational = this.currentOperationalValue();
    const occupied = this.currentOccupiedValue();
    return operational !== undefined && occupied !== undefined;
  });

  // Helpers for template to mark first/last logical fields in the full sequence
  isFirstOperationalField() {
    return this.index() === 0;
  }
  isLastOccupiedField() {
    return this.index() === this.totalRows() - 1;
  }
}
